import { Collapse, Tooltip } from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconInfoCircle, IconPhoto } from '@tabler/icons-react';
import clsx from 'clsx';
import { useState } from 'react';
import type { AiActionLog } from '~/types';

function AiActionLogItem({
  log,
  isSelected,
  onSelect,
}: {
  log: AiActionLog;
  isSelected: boolean;
  onSelect: (log: AiActionLog) => void;
}) {
  const hasAnyScreenshot = Boolean(log.beforeScreenshotUrl || log.actionScreenshotUrl || log.afterScreenshotUrl);

  return (
    <div
      className={clsx(
        'flex cursor-pointer items-center gap-2 rounded px-2 py-2 transition-colors',
        isSelected ? 'bg-gray-100 dark:bg-gray-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-900/20',
      )}
      onClick={() => onSelect(log)}
    >
      <span className="min-w-0 flex-1 text-xs text-gray-600 dark:text-gray-400">{log.description}</span>
      {hasAnyScreenshot ?
        <Tooltip label="View screenshot">
          <IconPhoto
            size={12}
            className={clsx(
              'flex-shrink-0 transition-colors',
              isSelected ? 'text-gray-500 dark:text-gray-400' : 'text-gray-400',
            )}
          />
        </Tooltip>
      : null}
      {log.error ?
        <span className="flex-shrink-0 text-xs text-red-500 dark:text-red-400">Error</span>
      : null}
    </div>
  );
}

/**
 * Displays AI action logs for a parent AI_ACTION operation.
 * Shows an expandable list of individual actions performed by the AI with optional screenshot viewing.
 * Logs should be pre-filtered by the caller for the specific parent operation.
 */
export default function AiActionLogDisplay({
  logs,
  defaultExpanded = false,
  selectOperationsForScreenshot,
  selectedLogId,
  onLogSelect,
  viewOnlyLabel,
}: {
  /** Pre-filtered logs for this specific AI_ACTION operation */
  logs: AiActionLog[];
  defaultExpanded?: boolean;
  selectOperationsForScreenshot?: (params: {
    originalOperationId?: string;
    newOperationId?: string;
    rightDirectScreenshotUrl?: string;
    rightDirectScreenshots?: { before?: string; action?: string; after?: string };
  }) => void;
  selectedLogId?: string;
  onLogSelect?: (logId: string | undefined) => void;
  viewOnlyLabel?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (logs.length === 0) {
    return null;
  }

  const handleLogSelect = (log: AiActionLog) => {
    const hasAnyScreenshot = Boolean(log.beforeScreenshotUrl || log.actionScreenshotUrl || log.afterScreenshotUrl);

    // Toggle selection - if clicking on already selected log, deselect it
    const isDeselecting = selectedLogId === log.id;
    onLogSelect?.(isDeselecting ? undefined : log.id);

    if (!selectOperationsForScreenshot) {
      return;
    }

    if (isDeselecting) {
      // Clear the screenshot display when deselecting
      selectOperationsForScreenshot({});
      return;
    }

    if (!hasAnyScreenshot) {
      return;
    }

    // Pass all three screenshots to enable Before/Action/After tabs
    selectOperationsForScreenshot({
      rightDirectScreenshots: {
        before: log.beforeScreenshotUrl,
        action: log.actionScreenshotUrl,
        after: log.afterScreenshotUrl,
      },
    });
  };

  const sortedLogs = logs.toSorted((a, b) => a.order - b.order);

  return (
    <div className="mt-1 border-t border-dashed border-violet-200 pt-1 dark:border-violet-800/50">
      {viewOnlyLabel ?
        <div className="mb-1 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <IconInfoCircle size={12} />
          <span>View Only - AI action logs (not a change to accept)</span>
        </div>
      : null}
      <button
        className="flex w-full items-center gap-2 py-1 text-left transition-colors hover:opacity-80"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ?
          <IconChevronDown
            size={14}
            className="text-violet-500 dark:text-violet-400"
          />
        : <IconChevronRight
            size={14}
            className="text-violet-500 dark:text-violet-400"
          />
        }
        <span className="text-xs text-violet-600 dark:text-violet-400">
          {logs.length} action{logs.length !== 1 ? 's' : ''} performed
        </span>
      </button>

      <Collapse in={expanded}>
        <div className="space-y-1 pb-1 pl-5">
          {sortedLogs.map((log) => (
            <AiActionLogItem
              key={log.id}
              log={log}
              isSelected={selectedLogId === log.id}
              onSelect={handleLogSelect}
            />
          ))}
        </div>
      </Collapse>
    </div>
  );
}
